import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpUtils} from '../../shared/http-utils';
import {UserInfo} from './user-info.model';
import {URL_BASE} from '../../core/constants';
import {UserCacheService, UserListType} from './user-cache.service';
import * as moment from 'moment';
import {map, tap} from 'rxjs/operators';
import {SearchObjectResult} from '../../shared/search-object-result.model';
import {UserSearchCriteria} from '../../shared/user-search-criteria';

@Injectable()
export class CarrierManagerService {

  private CARRIER_MANAGERS_URI = 'api/carrierManagers';
  private cacheListType        = UserListType.CARRIER_MANAGERS;

  constructor(private http: HttpClient, private cacheService: UserCacheService) {
  }

  public getByCarrier(carrierId: string): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/carrier/${carrierId}`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.cacheListType, (u) => u.carrier && u.carrier.id === carrierId))
      );
  }

  public searchByCarrier(carrierId: string, page: number, pageSize: number, filters: UserSearchCriteria): Observable<SearchObjectResult<UserInfo>> {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/carrier/${carrierId}/search`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto)),
      );
  }

  public getById(id: string): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/${id}`;
    return this.http.get(url, { headers })
      .pipe(
        map(user => UserInfo.fromDto(user))
      );
  }

  public create(manager: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}`;
    return this.http.post(url, UserInfo.toDto(manager), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.cacheListType))
      );
  }

  public update(manager: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/${manager.id}`;
    return this.http.put(url, UserInfo.toDto(manager), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerUpdatedUser(u, this.cacheListType))
      );
  }

  public updatePassword(manager: UserInfo, password: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/${manager.id}/password`;
    return this.http.post<void>(url, { password }, { headers });
  }

  public delete(managerId: string): Observable<boolean> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/${managerId}`;
    return this.http.delete<boolean>(url, { headers })
      .pipe(
        tap(() => this.cacheService.registerRemovedUser(managerId, this.cacheListType))
      );
  }

  public toggleBlocking(manager: UserInfo): Observable<boolean> {
    const updateDate = moment();

    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/${manager.id}/blocked/${!manager.blocked}`;
    return this.http.post<boolean>(url, null, { headers })
      .pipe(
        tap(() => {
          manager.updateDate          = updateDate.toISOString();
          manager.updateDateTimestamp = Number(updateDate.format('x'));
          this.cacheService.registerUpdatedUser(manager, this.cacheListType);
        })
      );
  }

  public unblockIp(manager: UserInfo): Observable<boolean> {
    const updateDate = moment();

    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.CARRIER_MANAGERS_URI}/${manager.id}/unblockFromAnomaly`;
    return this.http.post<boolean>(url, null, { headers })
      .pipe(
        tap(() => {
          manager.updateDate          = updateDate.toISOString();
          manager.updateDateTimestamp = Number(updateDate.format('x'));
          this.cacheService.registerUpdatedUser(manager, this.cacheListType);
        })
      );
  }

}
